import { useEffect } from 'react';
import { Coordinates, CalculationMethod, PrayerTimes } from 'adhan';
import { useProfile } from './useProfile';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Capacitor } from '@capacitor/core';

export function usePrayerAlerts() {
  const { profile } = useProfile();

  useEffect(() => {
    const isNative = Capacitor.isNativePlatform();

    const manageNotifications = async () => {
      // 1. Always cancel existing notifications first to ensure clean state
      if (isNative) {
        const pending = await LocalNotifications.getPending();
        if (pending.notifications.length > 0) {
            await LocalNotifications.cancel(pending);
        }
      }

      // 2. If notifications/alerts are disabled, stop here (we just cancelled everything)
      if (!profile.settings.notifications || !profile.settings.prayerAlerts) {
          console.log('Prayer alerts disabled. Cleared existing.');
          return;
      }

      // 3. Request Permissions
      if (isNative) {
        const perm = await LocalNotifications.requestPermissions();
        if (perm.display !== 'granted') return;
      } else {
        // Web check
        if (!('Notification' in window)) return;
        
        if (Notification.permission !== 'granted') {
          const permission = await Notification.requestPermission();
          if (permission !== 'granted') return;
        }
      }

      // 4. Get Location & Schedule
      if (!navigator.geolocation) return;

      navigator.geolocation.getCurrentPosition(async (position) => {
        const { latitude, longitude } = position.coords;
        const coordinates = new Coordinates(latitude, longitude);
        const params = CalculationMethod.MuslimWorldLeague();
        const pt = new PrayerTimes(coordinates, new Date(), params);

        const prayers = [
          { name: 'Fajr', time: pt.fajr, idBase: 100 },
          { name: 'Dhuhr', time: pt.dhuhr, idBase: 200 },
          { name: 'Asr', time: pt.asr, idBase: 300 },
          { name: 'Maghrib', time: pt.maghrib, idBase: 400 },
          { name: 'Isha', time: pt.isha, idBase: 500 },
        ];

        const notificationsToSchedule: any[] = [];
        const now = new Date();

        prayers.forEach(prayer => {
          // Calculate warning times (15, 10, 5 min + Exact Time)
          const times = [
            { minutes: 15, idOffset: 1, msg: `15 minutes to ${prayer.name}` },
            { minutes: 10, idOffset: 2, msg: `10 minutes to ${prayer.name}` },
            { minutes: 5, idOffset: 3, msg: `5 minutes to ${prayer.name}` },
            { minutes: 0, idOffset: 4, msg: `It's time for ${prayer.name}` } // The actual prayer time
          ];

          times.forEach(slot => {
            const notifyTime = new Date(prayer.time.getTime() - slot.minutes * 60000);
            
            // Only schedule if the time is in the future
            if (notifyTime > now) {
                if (isNative) {
                    notificationsToSchedule.push({
                        title: slot.minutes === 0 ? 'Prayer Time' : 'Prayer Reminder',
                        body: slot.msg,
                        id: prayer.idBase + slot.idOffset,
                        schedule: { at: notifyTime },
                        sound: slot.minutes === 0 ? 'default' : undefined, // Use system default sound for now
                        actionTypeId: '',
                        extra: null
                    });
                } else if ('Notification' in window && Notification.permission === 'granted') {
                    // Fallback for Web: Use setTimeout (less reliable if tab closes)
                    const delay = notifyTime.getTime() - now.getTime();
                    setTimeout(() => {
                        new Notification(slot.minutes === 0 ? 'Prayer Time' : 'Prayer Reminder', {
                            body: slot.msg,
                            icon: '/assets/logo.png'
                        });
                    }, delay);
                }
            }
          });
        });

        // Batch schedule for Native
        if (isNative && notificationsToSchedule.length > 0) {
            await LocalNotifications.schedule({ notifications: notificationsToSchedule });
            console.log('Scheduled', notificationsToSchedule.length, 'prayer notifications');
        }

      }, (err) => console.error('Location error for alerts:', err));
    };

    manageNotifications();
    
  }, [profile.settings.notifications, profile.settings.prayerAlerts]);
}